import { createSelector } from 'reselect';
import { graphColors, colorList, calcKwh, calcConsumption } from '../../constants';
import { strings } from '../../localization/index';
import { getSavedGroupById } from '../app/selector';

const getDeviceList = state => {
    if (state.devices.get('list')) {
        const list = state.devices.getIn(['list', 'data']);
        return list && list.toJS ? list.toJS() : list;
    }
    return undefined;
};

const getList = state => {
    if (state.training.get('list')) {
        return state.training.getIn(['list', 'data']);
    }
    return undefined;
};

const getSelected = state => {
    if (state.training.get('list')) {
        return state.training.getIn(['list', 'selected']);
    }
    return undefined;
};

const getZoomParams = state => state.training.get('zoom');

const getFilteredDevice = state => state.app.getIn(['smartTraining', 'filteredDevice']) || -2;

const getZoomEnabled = state => {
    if (state.training.get('zoom')) {
        return state.training.getIn(['zoom', 'enabled']);
    }
    return undefined;
};

const getHistoricalIsLoading = state => state.training.getIn(['list', 'isLoading']);

const getAssistant = state => {
    if (state.training.get('assistant')) {
        return state.training.getIn(['assistant', 'data']);
    }
    return undefined;
};

const getAssistantIsLoading = state => state.training.getIn(['assistant', 'isLoading']);

/* ====================== SELECTORS ======================== */

export const isZoomEnabled = createSelector(
    [getZoomEnabled],
    enabled => enabled
);

export const getSelectedList = createSelector([getSelected], list => list && list.toJS());

export const getHistoricalList = createSelector([getList], list => list && list.toJS());

export const getHistoricalEventsIsLoading = createSelector(
    [getHistoricalIsLoading],
    isLoading => isLoading
);

export const getAssistantList = createSelector([getAssistant], list => list && list.toJS());

export const getAssistantEventsIsLoading = createSelector(
    [getAssistantIsLoading],
    isLoading => isLoading
);

export const getHistoricalListSeparated = createSelector([getList, getSelected, getZoomParams, getFilteredDevice, getSavedGroupById, getDeviceList],
    (list, selected, zoomParams, filteredDevice, groupBy, deviceList) => {
    const records = list && list.toJS();
    const selectedItems = selected && selected.toJS();
    const zoom = zoomParams && zoomParams.toJS();
    let all = [];
    if (records)
        for(const record of records){
            // console.log(record);
            const obj = {
                device: record.device,
                x: record.watt,
                y: record.var || 0,
                nextPhaseTs: record.nextPhaseTs,
                phase: record.nextPhaseTs !== 0 ? 4 : record.phase,
                is3phase: record.nextPhaseTs !== 0,
                ts: record.id,
                id: record.device,
                duration: record.duration,
                p1ts: record.phase === 1 ? record.id : 0,
                p2ts: record.phase === 2 ? record.id : 0,
                p3ts: record.phase === 3 ? record.id : 0,
                selected: false,
                c5: record.c5,
                c10: record.c10,
                c20: record.c20
            };

            let dev = null;
            if (record.nextPhaseTs > 0) {
                // multi phase
                // check if it exists
                for (const li of all)
                    if (li.is3phase && li.nextPhaseTs > 0 && (li.ts === record.nextPhaseTs || li.nextPhaseTs === record.id)) {
                        dev = li;
                        break;
                    }
            }
            if (dev) {
                // change existing
                dev.x += record.watt;
                dev.y += record.var || 0;
                switch(record.phase){
                    case 1: dev.p1ts = record.id; break;
                    case 2: dev.p2ts = record.id; break;
                    case 3: dev.p3ts = record.id; break;
                    default:
                }
            } else {
                // add new
                all.push(obj);
            }
        }
    for(const item of all){
        item.selected = selectedItems.filter(i =>
            i.p1ts === item.p1ts &&
            i.p2ts === item.p2ts &&
            i.p3ts === item.p3ts).length > 0;
        item.hidden =
            filteredDevice !== -2 && item.device !== filteredDevice ||
            zoom.enabled && (
            item.x > zoom.xmax ||
            item.x < zoom.xmin ||
            item.y > zoom.ymax ||
            item.y < zoom.ymin );
    }
    // console.log(filteredDevice);
    all = all.filter(item => !item.hidden);
    // console.log(all);
    let datasets = [];
    switch(groupBy){
        case 1:
            // devices
        {
            const devIds = new Set();
            for(const item of all)
                if(item.device >= 0) devIds.add(item.device);
            // add unknown
            const unknown = all.filter(item => item.id < 0);
            if(unknown.length > 0) {
                const kwh = calcConsumption(all, -1);
                datasets.push({
                    kwh,
                    label: `[${kwh} kwh] ${strings.training.unknown}`,
                    bgColor: colorList[20].fill,
                    borderColor: colorList[20].border,
                    hoverColor: colorList[20].hover,
                    pointStyle: 'circle',
                    data: unknown
                });
            }
            all = all.filter(item => item.id >= 0);
            let devs = [...devIds];
            // add consumption
            const temp = [];
            for(const dev of devs){
                temp.push([dev, calcConsumption(all, dev)]);
            }
            temp.sort((a, b) => b[1] - a[1]);
            devs = temp;
            // devs.sort((a, b)=> a[1] - b[1]);
            let i = 0;
            for(i=0;i<devs.length && i<10;i+=1){
                let deviceName = `[${devs[i]}]`;
                if(deviceList){
                    const dev = deviceList.filter(item => item.id === devs[i][0]);
                    if(dev && dev.length > 0)
                        deviceName = dev[0].name;
                }
                datasets.push({
                    kwh: devs[i][1],
                    label: `[${devs[i][1]} kwh] ${deviceName}`,
                    bgColor: colorList[i].fill,
                    borderColor: colorList[i].border,
                    hoverColor: colorList[i].hover,
                    pointStyle: 'circle',
                    data: all.filter(item => item.id === devs[i][0])
                });
                all = all.filter(item => item.id !== devs[i][0]);
            }
            if(i<devs.length){
                const kwh = calcKwh(all);
                datasets.push({
                    kwh,
                    label: `[${kwh} kwh] ${strings.training.others}`,
                    bgColor: colorList[i].fill,
                    borderColor: colorList[i].border,
                    hoverColor: colorList[i].hover,
                    pointStyle: 'circle',
                    data: all
                });
            }
        }
            break;
        case 2:
            // 5 clusters
        case 3:
            // 10 clusters
        case 4:
            // 20 clusters
            {
                const clusterCount = groupBy === 2 ? 5 : (groupBy === 3 ? 10 : 20);
                const clusterBy = `c${clusterCount}`;
                for (let i = 0; i < clusterCount; i += 1) {
                    const points = all.filter(item => item[clusterBy] === i);
                    const kwh = calcKwh(points);
                    datasets.push({
                        kwh,
                        label: `[${kwh} kwh] ${strings.training.cluster}`,
                        bgColor: colorList[i].fill,
                        borderColor: colorList[i].border,
                        hoverColor: colorList[i].hover,
                        pointStyle: 'circle',
                        data: points
                    });
                }
            }
            break;
        case 0:
            // phases
            datasets = [{
                    kwh: 0,
                    label: strings.training.l1unknown,
                    bgColor: graphColors.p1_fill,
                    borderColor: graphColors.p1_line,
                    hoverColor: graphColors.p1_highlight,
                    pointStyle: 'circle',
                    data: all.filter(item => !item.is3phase && item.phase === 1 && item.id < 0 && !item.hidden)
                },{
                    kwh: 0,
                    label: strings.training.l2unknown,
                    bgColor: graphColors.p2_fill,
                    borderColor: graphColors.p2_line,
                    hoverColor: graphColors.p2_highlight,
                    pointStyle: 'circle',
                    data: all.filter(item => !item.is3phase && item.phase === 2 && item.id < 0 && !item.hidden)
                },{
                    kwh: 0,
                    label: strings.training.l3unknown,
                    bgColor: graphColors.p3_fill,
                    borderColor: graphColors.p3_line,
                    hoverColor: graphColors.p3_highlight,
                    pointStyle: 'circle',
                    data: all.filter(item => !item.is3phase && item.phase === 3 && item.id < 0 && !item.hidden)
                },{
                    kwh: 0,
                    label: strings.training.l123unknown,
                    bgColor: graphColors.oa_fill,
                    borderColor: graphColors.oa_line,
                    hoverColor: graphColors.oa_highlight,
                    pointStyle: 'circle',
                    data: all.filter(item => item.is3phase && item.id < 0 && !item.hidden)
                },{
                    kwh: 0,
                    label: strings.training.l123devices,
                    bgColor: graphColors.oa_highlight,
                    borderColor: graphColors.oa_line,
                    hoverColor: graphColors.oa_highlight,
                    pointStyle: 'circle',
                    data: all.filter(item => item.is3phase && item.id >= 0 && !item.hidden)
                },{
                    kwh: 0,
                    label: strings.training.l3devices,
                    bgColor: graphColors.p3_highlight,
                    borderColor: graphColors.p3_line,
                    hoverColor: graphColors.p3_highlight,
                    pointStyle: 'circle',
                    data: all.filter(item => !item.is3phase && item.phase === 3 && item.id >= 0 && !item.hidden)
                },{
                    kwh: 0,
                    label: strings.training.l2devices,
                    bgColor: graphColors.p2_highlight,
                    borderColor: graphColors.p2_line,
                    hoverColor: graphColors.p2_highlight,
                    pointStyle: 'circle',
                    data: all.filter(item => !item.is3phase && item.phase === 2 && item.id >= 0 && !item.hidden)
                },{
                    kwh: 0,
                    label: strings.training.l1devices,
                    bgColor: graphColors.p1_highlight,
                    borderColor: graphColors.p1_line,
                    hoverColor: graphColors.p1_highlight,
                    pointStyle: 'circle',
                    data: all.filter(item => !item.is3phase && item.phase === 1 && item.id >= 0 && !item.hidden)
                }];
            break;
        default:
    }
    datasets.sort((a,b) => b.kwh - a.kwh);
    if(groupBy > 1){
        // clustering
        for(let i = 0; i < datasets.length; i += 1)
            datasets[i].label = `${datasets[i].label} ${i + 1}`;
    }
    return {datasets};
});
